import {Component, computed, effect, inject, input, output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TranslatePipe} from '@ngx-translate/core';
import {CustomerProductService, CustomerProductSummary, LicenceServerSummary} from '../../../../../../../api';
import {QueryKey} from '../../../../../../constants/query-key';
import {TextInput} from '../../../../../../components/text-input/text-input';
import {MultiDropdown} from '../../../../../../components/multi-dropdown/multi-dropdown';

export interface LicenceServerFormValue {
  name: string;
  customerProductIds: number[];
}

@Component({
  selector: 'elcia-licence-server-dialog',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    TextInput,
    MultiDropdown,
  ],
  templateUrl: './licence-server-dialog.html',
})
export class LicenceServerDialog {
  customerId = input<number | null>(null);
  initialValue = input<LicenceServerSummary | null>(null);
  onSubmit = output<LicenceServerFormValue>();

  private readonly customerProductService = inject(CustomerProductService);

  private readonly customerProductsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT_CUSTOMERS, this.customerId()],
    queryFn: () => lastValueFrom(this.customerProductService.getCustomerProductsByCustomerId(this.customerId()!)),
    enabled: this.customerId() !== null,
  }));

  protected readonly customerProducts = computed<CustomerProductSummary[]>(() =>
    this.customerProductsQuery.data()?.customerProducts ?? []);

  protected readonly form = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required),
    customerProductIds: new FormControl<number[]>([], {nonNullable: true}),
  });

  private readonly onInitialValueChange = effect(() => {
    const server = this.initialValue();
    if (!server) {
      this.form.reset({customerProductIds: []});
      return;
    }

    // Les produits déjà assignés sont les licences et plugins du serveur (les features suivent leur licence parente).
    const assignedIds = [
      ...server.products.licences.map(licence => licence.id),
      ...server.products.plugins.map(plugin => plugin.id),
    ];

    this.form.reset({
      name: server.name,
      customerProductIds: assignedIds,
    });
  });

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    this.onSubmit.emit({
      name: raw.name!,
      customerProductIds: raw.customerProductIds,
    });
  }
}
